import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import SearchIcon from "@mui/icons-material/Search";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    ButtonBase,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    InputAdornment,
    LinearProgress,
    Paper,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { formatDuration } from "./dashboardFormatters";
import type { OrganizationDto } from "../../api/bpm/model";
import type {
    OrganizationDashboardData,
    OrganizationDashboardItem,
    OrganizationDashboardTrend,
} from "../../api/bpm/organizationDashboard";
import { useGetOrganizationDashboardUsers } from "../../api/bpm/organizationDashboard";

interface OrganizationDashboardProps {
    data?: OrganizationDashboardData;
    organizations: OrganizationDto[];
    selectedOrganizationKeys: string[];
    onOrganizationsChange: (keys: string[]) => void;
    loading: boolean;
    error: boolean;
    onProcessMetricClick?: (metric: "started" | "completed" | "incidents") => void;
    onOrganizationOpen?: (organizationKey: string, metric?: "started" | "incidents") => void;
}

type SortField = keyof Pick<
    OrganizationDashboardItem,
    | "organizationName"
    | "processInstancesStarted"
    | "activeProcessInstances"
    | "tasksStarted"
    | "pendingTasks"
    | "overdueTasks"
    | "incidentsCount"
    | "completionRate"
    | "slaComplianceRate"
    | "averageProcessDurationInMillis"
    | "lastMovementAt"
>;

const numberFormatter = new Intl.NumberFormat("pt-BR");
const LOW_COMPLETION_RATE = 60;

export default function OrganizationDashboard({
    data,
    organizations,
    selectedOrganizationKeys,
    onOrganizationsChange,
    loading,
    error,
    onProcessMetricClick,
    onOrganizationOpen,
}: OrganizationDashboardProps) {
    const { t, i18n } = useTranslation();
    const [search, setSearch] = useState("");
    const [sortField, setSortField] = useState<SortField>("overdueTasks");
    const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [detailsOrganization, setDetailsOrganization] = useState<OrganizationDashboardItem | null>(null);
    const [detailsPage, setDetailsPage] = useState(0);
    const [detailsRowsPerPage, setDetailsRowsPerPage] = useState(5);
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const userDetailsQuery = useGetOrganizationDashboardUsers(
        {
            organizationKey: detailsOrganization?.organizationKey ?? "",
            startDate: data?.periodStartDate ?? "",
            endDate: data?.periodEndDate ?? "",
            timeZone,
        },
        Boolean(detailsOrganization && data?.periodStartDate && data?.periodEndDate),
    );

    const enabledOrganizations = useMemo(
        () => organizations.filter((organization) => organization.enabled !== false && organization.key),
        [organizations],
    );
    const selectedOrganizations = enabledOrganizations.filter((organization) =>
        selectedOrganizationKeys.includes(organization.key ?? ""),
    );

    const rows = useMemo(() => {
        const normalizedSearch = search.trim().toLocaleLowerCase();
        return [...(data?.organizations ?? [])]
            .filter((item) => item.organizationName.toLocaleLowerCase().includes(normalizedSearch))
            .sort((left, right) => {
                const leftValue = left[sortField];
                const rightValue = right[sortField];
                let comparison: number;
                if (sortField === "lastMovementAt") {
                    comparison = safeNumber(new Date(leftValue?.toString() ?? 0).getTime())
                        - safeNumber(new Date(rightValue?.toString() ?? 0).getTime());
                } else if (typeof leftValue === "string") {
                    comparison = leftValue.localeCompare(String(rightValue), undefined, { sensitivity: "base" });
                } else {
                    comparison = safeNumber(typeof leftValue === "number" ? leftValue : undefined)
                        - safeNumber(typeof rightValue === "number" ? rightValue : undefined);
                }
                return sortDirection === "asc" ? comparison : -comparison;
            });
    }, [data?.organizations, search, sortDirection, sortField]);

    const comparisonRows = useMemo(
        () => [...(data?.organizations ?? [])]
            .sort((a, b) => safeNumber(b.processInstancesStarted) - safeNumber(a.processInstancesStarted))
            .slice(0, 10),
        [data?.organizations],
    );
    const comparisonMaximum = Math.max(
        1,
        ...comparisonRows.flatMap((item) => [
            safeNumber(item.processInstancesStarted),
            safeNumber(item.processInstancesCompleted),
        ]),
    );

    const attentionRows = useMemo(
        () => [...(data?.organizations ?? [])]
            .filter((item) => safeNumber(item.overdueTasks) > 0
                || safeNumber(item.incidentsCount) > 0
                || item.lastMovementAt == null
                || (safeNumber(item.processInstancesStarted) > 0 && safeNumber(item.completionRate) < LOW_COMPLETION_RATE))
            .sort(compareAttention)
            .slice(0, 5),
        [data?.organizations],
    );

    const handleSort = (field: SortField) => {
        if (field === sortField) {
            setSortDirection((current) => current === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortDirection("desc");
        }
        setPage(0);
    };

    return (
        <Box sx={{ display: "grid", gap: 2.5, minWidth: 0, maxWidth: "100%" }}>
            <Paper variant="outlined" sx={{ p: 2, minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
                <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={enabledOrganizations}
                    value={selectedOrganizations}
                    getOptionLabel={(option) => option.name || option.key || ""}
                    isOptionEqualToValue={(option, value) => option.key === value.key}
                    onChange={(_, value) => onOrganizationsChange(value.flatMap((item) => item.key ? [item.key] : []))}
                    renderOption={(props, option, state) => {
                        const { key, ...optionProps } = props;
                        return (
                            <li key={key} {...optionProps}>
                                <Checkbox checked={state.selected} sx={{ mr: 1 }} />
                                {option.name || option.key}
                            </li>
                        );
                    }}
                    renderTags={(value, getTagProps) => value.slice(0, 2).map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return <Chip key={key} label={option.name || option.key} size="small" {...tagProps} />;
                    }).concat(value.length > 2 ? [<Chip key="more" size="small" label={`+${value.length - 2}`} />] : [])}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            size="small"
                            label={t("organizationDashboard.organizationFilter")}
                            placeholder={selectedOrganizations.length === 0
                                ? t("organizationDashboard.allOrganizations")
                                : undefined}
                            helperText={t("organizationDashboard.organizationFilterHelp")}
                        />
                    )}
                />
            </Paper>

            {error ? <Alert severity="error">{t("organizationDashboard.loadError")}</Alert> : null}

            <DashboardSectionTitle
                title={t("organizationDashboard.periodIndicators")}
                subtitle={t("organizationDashboard.periodIndicatorsHelp")}
            />
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)", xl: "repeat(6, 1fr)" }, gap: 2 }}>
                <MetricCard icon={<BusinessOutlinedIcon />} label={t("organizationDashboard.organizations")} value={data?.organizationsCount} loading={loading} />
                <MetricCard icon={<AccountTreeOutlinedIcon />} label={t("organizationDashboard.processesStarted")} value={data?.processInstancesStarted} loading={loading} onClick={onProcessMetricClick ? () => onProcessMetricClick("started") : undefined} />
                <MetricCard icon={<CheckCircleOutlineIcon />} label={t("organizationDashboard.processesCompleted")} value={data?.processInstancesCompleted} loading={loading} onClick={onProcessMetricClick ? () => onProcessMetricClick("completed") : undefined} />
                <MetricCard icon={<TaskAltOutlinedIcon />} label={t("organizationDashboard.tasksStarted")} value={data?.tasksStarted} loading={loading} />
                <MetricCard icon={<CheckCircleOutlineIcon />} label={t("organizationDashboard.completionRate")} value={formatPercentage(data?.completionRate, i18n.language)} loading={loading} />
                <MetricCard icon={<BugReportOutlinedIcon />} label={t("organizationDashboard.incidents")} value={data?.incidentsCount} loading={loading} tone="error" onClick={onProcessMetricClick ? () => onProcessMetricClick("incidents") : undefined} />
            </Box>

            <DashboardSectionTitle
                title={t("organizationDashboard.currentSituation")}
                subtitle={t("organizationDashboard.currentSituationHelp")}
            />
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)", xl: "repeat(6, 1fr)" }, gap: 2 }}>
                <MetricCard icon={<TimelineOutlinedIcon />} label={t("organizationDashboard.activeProcesses")} value={data?.activeProcessInstances} loading={loading} />
                <MetricCard icon={<PendingActionsOutlinedIcon />} label={t("organizationDashboard.pendingTasks")} value={data?.pendingTasks} loading={loading} />
                <MetricCard icon={<ErrorOutlineIcon />} label={t("organizationDashboard.overdueTasks")} value={data?.overdueTasks} loading={loading} tone="error" />
                <MetricCard icon={<CheckCircleOutlineIcon />} label={t("organizationDashboard.slaCompliance")} value={formatPercentage(data?.slaComplianceRate, i18n.language)} loading={loading} tone="success" />
                <MetricCard icon={<AccessTimeOutlinedIcon />} label={t("organizationDashboard.averageDuration")} value={formatDuration(data?.averageProcessDurationInMillis)} loading={loading} />
                <MetricCard icon={<WarningAmberOutlinedIcon />} label={t("organizationDashboard.withoutMovement")} value={data?.organizationsWithoutMovement} loading={loading} tone="warning" />
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 2fr) minmax(300px, 1fr)" }, gap: 2 }}>
                <Paper variant="outlined" sx={{ p: 2.5, minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
                    <Typography variant="h6">{t("organizationDashboard.evolutionTitle")}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                        {t("organizationDashboard.evolutionSubtitle")}
                    </Typography>
                    {loading ? <Skeleton height={270} /> : (data?.trend?.length ?? 0) === 0 ? (
                        <Typography color="text.secondary">{t("organizationDashboard.noData")}</Typography>
                    ) : (
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "repeat(2, minmax(0, 1fr))" }, gap: 3 }}>
                            <TrendChart
                                title={t("organizationDashboard.processEvolution")}
                                points={data?.trend ?? []}
                                aggregation={data?.trendAggregation ?? "DAY"}
                                startedValue={(point) => point.processInstancesStarted}
                                completedValue={(point) => point.processInstancesCompleted}
                                locale={i18n.language}
                                startedLabel={t("organizationDashboard.startedShort")}
                                completedLabel={t("organizationDashboard.completedShort")}
                            />
                            <TrendChart
                                title={t("organizationDashboard.taskEvolution")}
                                points={data?.trend ?? []}
                                aggregation={data?.trendAggregation ?? "DAY"}
                                startedValue={(point) => point.tasksStarted}
                                completedValue={(point) => point.tasksCompleted}
                                locale={i18n.language}
                                startedLabel={t("organizationDashboard.startedShort")}
                                completedLabel={t("organizationDashboard.completedShort")}
                            />
                        </Box>
                    )}
                </Paper>

                <Paper variant="outlined" sx={{ p: 2.5, minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
                    <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
                        <WarningAmberOutlinedIcon color="warning" />
                        <Typography variant="h6">{t("organizationDashboard.attentionTitle")}</Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {t("organizationDashboard.attentionSubtitle")}
                    </Typography>
                    {loading ? <Stack gap={1.5}>{[1, 2, 3].map((item) => <Skeleton key={item} height={60} />)}</Stack>
                        : attentionRows.length === 0 ? (
                            <Alert severity="success" variant="outlined">{t("organizationDashboard.noAttention")}</Alert>
                        ) : (
                            <Stack gap={1.25}>
                                {attentionRows.map((item) => (
                                    <Box key={item.organizationKey} sx={{ p: 1.5, borderRadius: 2, bgcolor: "action.hover" }}>
                                        <Typography variant="body2" fontWeight={700} noWrap title={item.organizationName}>
                                            {item.organizationName}
                                        </Typography>
                                        <Stack direction="row" gap={0.75} flexWrap="wrap" mt={1}>
                                            {safeNumber(item.overdueTasks) > 0 ? <Chip size="small" color="error" variant="outlined" label={t("organizationDashboard.overdueCount", { count: safeNumber(item.overdueTasks) })} /> : null}
                                            {safeNumber(item.incidentsCount) > 0 ? <Chip size="small" color="error" variant="outlined" label={t("organizationDashboard.incidentCount", { count: safeNumber(item.incidentsCount) })} onClick={onOrganizationOpen ? () => onOrganizationOpen(item.organizationKey, "incidents") : undefined} /> : null}
                                            {safeNumber(item.processInstancesStarted) > 0 && safeNumber(item.completionRate) < LOW_COMPLETION_RATE ? <Chip size="small" color="warning" variant="outlined" label={t("organizationDashboard.lowCompletion", { rate: formatPercentage(item.completionRate, i18n.language) })} /> : null}
                                            {!item.lastMovementAt ? <Chip size="small" variant="outlined" label={t("organizationDashboard.noMovementInPeriod")} /> : null}
                                        </Stack>
                                    </Box>
                                ))}
                            </Stack>
                        )}
                </Paper>
            </Box>

            <Paper variant="outlined" sx={{ p: 2.5, minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
                <Typography variant="h6" sx={{ mb: 0.5 }}>{t("organizationDashboard.comparisonTitle")}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                    {t("organizationDashboard.comparisonSubtitle")}
                </Typography>
                {loading ? <CircularProgress size={28} /> : comparisonRows.length === 0 ? (
                    <Typography color="text.secondary">{t("organizationDashboard.noData")}</Typography>
                ) : comparisonRows.map((item) => (
                    <Box key={item.organizationKey} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(160px, 260px) 1fr" }, gap: { xs: 0.75, md: 2 }, mb: 2 }}>
                        <Typography variant="body2" fontWeight={600} noWrap title={item.organizationName}>{item.organizationName}</Typography>
                        <Box sx={{ display: "grid", gap: 0.75 }}>
                            <MetricBar label={t("organizationDashboard.startedShort")} value={safeNumber(item.processInstancesStarted)} maximum={comparisonMaximum} color="primary" />
                            <MetricBar label={t("organizationDashboard.completedShort")} value={safeNumber(item.processInstancesCompleted)} maximum={comparisonMaximum} color="success" />
                        </Box>
                    </Box>
                ))}
            </Paper>

            <Paper variant="outlined" sx={{ minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
                <Box sx={{ p: 2.5, display: "flex", alignItems: { xs: "stretch", sm: "center" }, justifyContent: "space-between", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
                    <Box>
                        <Typography variant="h6">{t("organizationDashboard.tableTitle")}</Typography>
                        <Typography variant="body2" color="text.secondary">{t("organizationDashboard.tableSubtitle")}</Typography>
                    </Box>
                    <TextField
                        size="small"
                        value={search}
                        onChange={(event) => { setSearch(event.target.value); setPage(0); }}
                        placeholder={t("organizationDashboard.search")}
                        slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
                    />
                </Box>
                <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
                    <Table size="small" sx={{ minWidth: 1380 }}>
                        <TableHead>
                            <TableRow>
                                <SortableHeader field="organizationName" activeField={sortField} direction={sortDirection} onSort={handleSort}>{t("organizationDashboard.organization")}</SortableHeader>
                                <SortableHeader field="processInstancesStarted" activeField={sortField} direction={sortDirection} onSort={handleSort} align="right">{t("organizationDashboard.processesInPeriod")}</SortableHeader>
                                <SortableHeader field="activeProcessInstances" activeField={sortField} direction={sortDirection} onSort={handleSort} align="right">{t("organizationDashboard.activeProcesses")}</SortableHeader>
                                <SortableHeader field="tasksStarted" activeField={sortField} direction={sortDirection} onSort={handleSort} align="right">{t("organizationDashboard.tasksInPeriod")}</SortableHeader>
                                <SortableHeader field="pendingTasks" activeField={sortField} direction={sortDirection} onSort={handleSort} align="right">{t("organizationDashboard.pendingTasks")}</SortableHeader>
                                <SortableHeader field="overdueTasks" activeField={sortField} direction={sortDirection} onSort={handleSort} align="right">{t("organizationDashboard.overdueTasks")}</SortableHeader>
                                <SortableHeader field="incidentsCount" activeField={sortField} direction={sortDirection} onSort={handleSort} align="right">{t("organizationDashboard.incidents")}</SortableHeader>
                                <SortableHeader field="completionRate" activeField={sortField} direction={sortDirection} onSort={handleSort} align="right">{t("organizationDashboard.completionRate")}</SortableHeader>
                                <SortableHeader field="slaComplianceRate" activeField={sortField} direction={sortDirection} onSort={handleSort} align="right">{t("organizationDashboard.slaCompliance")}</SortableHeader>
                                <SortableHeader field="averageProcessDurationInMillis" activeField={sortField} direction={sortDirection} onSort={handleSort} align="right">{t("organizationDashboard.averageDuration")}</SortableHeader>
                                <SortableHeader field="lastMovementAt" activeField={sortField} direction={sortDirection} onSort={handleSort}>{t("organizationDashboard.lastMovement")}</SortableHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? Array.from({ length: 4 }).map((_, index) => (
                                <TableRow key={index}><TableCell colSpan={11}><Skeleton /></TableCell></TableRow>
                            )) : rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item) => (
                                <TableRow key={item.organizationKey} hover>
                                    <TableCell>
                                        <ButtonBase
                                            onClick={() => {
                                                setDetailsOrganization(item);
                                                setDetailsPage(0);
                                            }}
                                            sx={{ color: "primary.main", fontWeight: 700, textAlign: "left", borderRadius: 1 }}
                                        >
                                            {item.organizationName}
                                        </ButtonBase>
                                    </TableCell>
                                    <TableCell align="right">{formatRatio(item.processInstancesCompleted, item.processInstancesStarted)}</TableCell>
                                    <TableCell align="right">{numberFormatter.format(safeNumber(item.activeProcessInstances))}</TableCell>
                                    <TableCell align="right">{formatRatio(item.tasksCompleted, item.tasksStarted)}</TableCell>
                                    <TableCell align="right">{numberFormatter.format(safeNumber(item.pendingTasks))}</TableCell>
                                    <TableCell align="right"><Typography component="span" color={safeNumber(item.overdueTasks) > 0 ? "error.main" : "text.primary"} fontWeight={safeNumber(item.overdueTasks) > 0 ? 700 : 400}>{numberFormatter.format(safeNumber(item.overdueTasks))}</Typography></TableCell>
                                    <TableCell align="right">
                                        {safeNumber(item.incidentsCount) > 0 && onOrganizationOpen ? (
                                            <Button color="error" size="small" onClick={() => onOrganizationOpen(item.organizationKey, "incidents")}>
                                                {numberFormatter.format(safeNumber(item.incidentsCount))}
                                            </Button>
                                        ) : numberFormatter.format(safeNumber(item.incidentsCount))}
                                    </TableCell>
                                    <TableCell align="right">{formatPercentage(item.completionRate, i18n.language)}</TableCell>
                                    <TableCell align="right">{formatPercentage(item.slaComplianceRate, i18n.language)}</TableCell>
                                    <TableCell align="right">{formatDuration(item.averageProcessDurationInMillis)}</TableCell>
                                    <TableCell>{item.lastMovementAt ? formatDateTime(item.lastMovementAt, i18n.language) : t("organizationDashboard.noMovement")}</TableCell>
                                </TableRow>
                            ))}
                            {!loading && rows.length === 0 ? (
                                <TableRow><TableCell colSpan={11} align="center" sx={{ py: 4, color: "text.secondary" }}>{t("organizationDashboard.noData")}</TableCell></TableRow>
                            ) : null}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={rows.length}
                    page={Math.min(page, Math.max(0, Math.ceil(rows.length / rowsPerPage) - 1))}
                    onPageChange={(_, nextPage) => setPage(nextPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0); }}
                    rowsPerPageOptions={[5, 10, 25]}
                />
            </Paper>

            <Dialog
                open={Boolean(detailsOrganization)}
                onClose={() => setDetailsOrganization(null)}
                fullWidth
                maxWidth="lg"
            >
                <DialogTitle>
                    {t("organizationDashboard.userDetailsTitle", {
                        organization: detailsOrganization?.organizationName ?? "",
                    })}
                </DialogTitle>
                <DialogContent dividers sx={{ minWidth: 0 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {t("organizationDashboard.userDetailsHelp", {
                            startDate: formatDate(data?.periodStartDate, i18n.language),
                            endDate: formatDate(data?.periodEndDate, i18n.language),
                        })}
                    </Typography>
                    {userDetailsQuery.isLoading ? (
                        <Stack role="status" alignItems="center" sx={{ py: 5 }}><CircularProgress size={36} /></Stack>
                    ) : userDetailsQuery.isError ? (
                        <Alert severity="error">{t("organizationDashboard.userDetailsLoadError")}</Alert>
                    ) : (userDetailsQuery.data?.users.length ?? 0) === 0 ? (
                        <Alert severity="info">{t("organizationDashboard.noUserDetails")}</Alert>
                    ) : (
                        <>
                            <TableContainer sx={{ maxWidth: "100%", maxHeight: 440, overflow: "auto" }}>
                                <Table size="small" stickyHeader sx={{ minWidth: 980 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t("organizationDashboard.initiatingUser")}</TableCell>
                                            <TableCell align="right">{t("organizationDashboard.startedShort")}</TableCell>
                                            <TableCell align="right">{t("organizationDashboard.openProcesses")}</TableCell>
                                            <TableCell align="right">{t("organizationDashboard.completedShort")}</TableCell>
                                            <TableCell align="right">{t("organizationDashboard.rework")}</TableCell>
                                            <TableCell align="right">{t("organizationDashboard.completionRate")}</TableCell>
                                            <TableCell align="right">{t("organizationDashboard.averageDuration")}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {userDetailsQuery.data?.users
                                            .slice(detailsPage * detailsRowsPerPage, detailsPage * detailsRowsPerPage + detailsRowsPerPage)
                                            .map((user) => (
                                                <TableRow key={user.userId ?? "no-start-user"} hover>
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight={600}>{user.userName}</Typography>
                                                        {user.userEmail || user.userId ? (
                                                            <Typography variant="caption" color="text.secondary">
                                                                {user.userEmail || user.userId}
                                                            </Typography>
                                                        ) : null}
                                                    </TableCell>
                                                    <TableCell align="right">{numberFormatter.format(safeNumber(user.processInstancesStarted))}</TableCell>
                                                    <TableCell align="right">{numberFormatter.format(safeNumber(user.activeProcessInstances))}</TableCell>
                                                    <TableCell align="right">{numberFormatter.format(safeNumber(user.processInstancesCompleted))}</TableCell>
                                                    <TableCell align="right">
                                                        {numberFormatter.format(safeNumber(user.reworkedProcessInstances))}
                                                        {` (${formatPercentage(user.reworkRate, i18n.language)})`}
                                                    </TableCell>
                                                    <TableCell align="right">{formatPercentage(user.completionRate, i18n.language)}</TableCell>
                                                    <TableCell align="right">{formatDuration(user.averageProcessDurationInMillis)}</TableCell>
                                                </TableRow>
                                            ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                component="div"
                                count={userDetailsQuery.data?.users.length ?? 0}
                                page={detailsPage}
                                onPageChange={(_, nextPage) => setDetailsPage(nextPage)}
                                rowsPerPage={detailsRowsPerPage}
                                onRowsPerPageChange={(event) => {
                                    setDetailsRowsPerPage(Number(event.target.value));
                                    setDetailsPage(0);
                                }}
                                rowsPerPageOptions={[5, 10, 25]}
                            />
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailsOrganization(null)}>{t("common.close")}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

function DashboardSectionTitle({ title, subtitle }: { title: string; subtitle: string }) {
    return (
        <Box sx={{ mt: 0.5 }}>
            <Typography variant="h6">{title}</Typography>
            <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
        </Box>
    );
}

function MetricCard({ icon, label, value, loading, tone = "primary", onClick }: { icon: React.ReactNode; label: string; value?: string | number; loading: boolean; tone?: "primary" | "success" | "warning" | "error"; onClick?: () => void }) {
    return (
        <Paper
            variant="outlined"
            component={onClick ? "button" : "div"}
            type={onClick ? "button" : undefined}
            onClick={onClick}
            sx={{
                p: 2,
                minHeight: 112,
                minWidth: 0,
                maxWidth: "100%",
                overflow: "hidden",
                width: "100%",
                textAlign: "left",
                font: "inherit",
                cursor: onClick ? "pointer" : "default",
                transition: "border-color 160ms ease, transform 160ms ease",
                "&:hover": onClick ? { borderColor: `${tone}.main`, transform: "translateY(-1px)" } : undefined,
                "&:focus-visible": onClick ? { outline: "2px solid", outlineColor: `${tone}.main`, outlineOffset: 2 } : undefined,
            }}
        >
            <Box sx={{ color: `${tone}.main`, mb: 1 }}>{icon}</Box>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            {loading ? <Skeleton width={72} height={38} /> : (
                <Typography variant="h5" fontWeight={700}>{typeof value === "number" ? numberFormatter.format(safeNumber(value)) : value ?? "0"}</Typography>
            )}
        </Paper>
    );
}

function MetricBar({ label, value, maximum, color }: { label: string; value: number; maximum: number; color: "primary" | "success" }) {
    const normalizedValue = safeNumber(value);
    const normalizedMaximum = Math.max(1, safeNumber(maximum));
    return (
        <Box sx={{ display: "grid", gridTemplateColumns: "72px minmax(0, 1fr) 52px", gap: 1, alignItems: "center", minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <LinearProgress variant="determinate" value={normalizedValue * 100 / normalizedMaximum} color={color} sx={{ height: 8, borderRadius: 4 }} />
            <Typography variant="caption" fontWeight={600} textAlign="right">{numberFormatter.format(normalizedValue)}</Typography>
        </Box>
    );
}

function TrendChart({ title, points, aggregation, startedValue, completedValue, locale, startedLabel, completedLabel }: {
    title: string;
    points: OrganizationDashboardTrend[];
    aggregation: OrganizationDashboardData["trendAggregation"];
    startedValue: (point: OrganizationDashboardTrend) => number;
    completedValue: (point: OrganizationDashboardTrend) => number;
    locale?: string;
    startedLabel: string;
    completedLabel: string;
}) {
    const maximum = Math.max(1, ...points.flatMap((point) => [
        safeNumber(startedValue(point)),
        safeNumber(completedValue(point)),
    ]));
    return (
        <Box data-testid={`organization-trend-${title}`} minWidth={0} maxWidth="100%" overflow="hidden">
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} mb={1.5}>
                <Typography variant="subtitle2">{title}</Typography>
                <Stack direction="row" gap={1.5}>
                    <TrendLegend label={startedLabel} color="primary.main" />
                    <TrendLegend label={completedLabel} color="success.main" />
                </Stack>
            </Stack>
            <Box sx={{ overflowX: "auto", pb: 0.5 }}>
                <Stack direction="row" alignItems="flex-end" sx={{ minWidth: Math.max(340, points.length * 58), height: 210 }}>
                    {points.map((point) => {
                        const started = safeNumber(startedValue(point));
                        const completed = safeNumber(completedValue(point));
                        const label = formatTrendLabel(point.startDate, point.endDate, aggregation, locale);
                        return (
                            <Tooltip key={`${point.startDate}-${title}`} arrow title={`${label}: ${startedLabel} ${started}, ${completedLabel} ${completed}`}>
                                <Stack role="img" aria-label={`${label}: ${startedLabel} ${started}, ${completedLabel} ${completed}`} alignItems="center" justifyContent="flex-end" sx={{ flex: 1, minWidth: 58, height: "100%" }}>
                                    <Stack direction="row" alignItems="flex-end" justifyContent="center" gap={0.5} sx={{ flex: 1, width: "100%" }}>
                                        <TrendBar value={started} maximum={maximum} color="primary.main" />
                                        <TrendBar value={completed} maximum={maximum} color="success.main" />
                                    </Stack>
                                    <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 56 }}>{label}</Typography>
                                </Stack>
                            </Tooltip>
                        );
                    })}
                </Stack>
            </Box>
        </Box>
    );
}

function TrendBar({ value, maximum, color }: { value: number; maximum: number; color: string }) {
    const height = value === 0 ? 2 : Math.max(5, value * 100 / maximum);
    return <Box sx={{ width: 12, height: `${height}%`, maxHeight: 174, bgcolor: color, borderRadius: "4px 4px 0 0", opacity: value === 0 ? 0.2 : 1 }} />;
}

function TrendLegend({ label, color }: { label: string; color: string }) {
    return (
        <Stack direction="row" alignItems="center" gap={0.5}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color }} />
            <Typography variant="caption" color="text.secondary">{label}</Typography>
        </Stack>
    );
}

function SortableHeader({ field, activeField, direction, onSort, align, children }: { field: SortField; activeField: SortField; direction: "asc" | "desc"; onSort: (field: SortField) => void; align?: "right"; children: React.ReactNode }) {
    return (
        <TableCell align={align} sortDirection={activeField === field ? direction : false}>
            <TableSortLabel active={activeField === field} direction={activeField === field ? direction : "asc"} onClick={() => onSort(field)}>
                {children}
            </TableSortLabel>
        </TableCell>
    );
}

function compareAttention(left: OrganizationDashboardItem, right: OrganizationDashboardItem) {
    return safeNumber(right.incidentsCount) - safeNumber(left.incidentsCount)
        || safeNumber(right.overdueTasks) - safeNumber(left.overdueTasks)
        || Number(!right.lastMovementAt) - Number(!left.lastMovementAt)
        || safeNumber(left.completionRate) - safeNumber(right.completionRate)
        || left.organizationName.localeCompare(right.organizationName);
}

function safeNumber(value?: number | null) {
    return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function formatPercentage(value?: number | null, locale?: string) {
    return `${safeNumber(value).toLocaleString(locale || "pt-BR", { maximumFractionDigits: 1 })}%`;
}

function formatDate(value?: string, locale?: string) {
    if (!value) return "-";
    return new Intl.DateTimeFormat(locale || "pt-BR").format(new Date(`${value}T12:00:00`));
}

function formatRatio(completed?: number | null, started?: number | null) {
    return `${numberFormatter.format(safeNumber(completed))} / ${numberFormatter.format(safeNumber(started))}`;
}

function formatDateTime(value: string, locale?: string) {
    return new Intl.DateTimeFormat(locale || "pt-BR", { dateStyle: "short", timeStyle: "short" }).format(new Date(value));
}

function formatTrendLabel(startDate: string, endDate: string, aggregation: OrganizationDashboardData["trendAggregation"], locale?: string) {
    const start = new Date(`${startDate}T12:00:00`);
    const end = new Date(`${endDate}T12:00:00`);
    if (aggregation === "MONTH") {
        return new Intl.DateTimeFormat(locale || "pt-BR", { month: "short", year: "2-digit" }).format(start);
    }
    const formatter = new Intl.DateTimeFormat(locale || "pt-BR", { day: "2-digit", month: "short" });
    return aggregation === "WEEK" ? `${formatter.format(start)}–${formatter.format(end)}` : formatter.format(start);
}
